/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback } from 'react'
import { EditNotificationEventOptionsFn } from 'components/CIMSNotifications/types'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { useCASNotificationsContext } from '../../CASNotificationsContext'
import { useRecipientsSelectContext } from 'components/CIMSSelect/common/RecipientsSelect/RecipientsSelectContext'
import { Permission, FrequencyDefault } from '@cims-online/shared'
import RenderFormItems from './components/RenderFormItems'
import { DailyFrequency, useUpsertCasNotificationMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { ByRoutingGroupsFormItemValue } from './components/RenderFormItems/components/ByRoutingGroupsFormItem/types'
import { Moment } from 'moment'
import moment from 'moment'
import { useProjectContext } from 'context/ProjectContext'

export function useEdit() {
  const { projectId } = useProjectContext()
  const { recipients, refetch, casNotifications } = useCASNotificationsContext()
  const recipientsSelectContext = useRecipientsSelectContext()
  const [edit] = useUpsertCasNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      upsertCASNotification: {
        notification: { notificationEvent },
      },
    }) => simpleEditNotification(notificationEvent),
  })

  return useCallback<EditNotificationEventOptionsFn<InitialValues>>(
    (event) => {
      const { every, byRoutingGroup } = recipients(event)
      const initialFrequency = casNotifications?.find(
        (notification) => notification.notificationEventId === event.id
      )?.settings?.frequency
      return {
        permission: Permission.EditCASNotification,
        initialValues: {
          every: recipientsSelectContext.toLabeledValues(every),
          routingGroups: byRoutingGroup.map(({ routingGroup, recipients }) => ({
            routingGroupId: routingGroup.id,
            recipients: recipientsSelectContext.toLabeledValues(recipients),
          })),
          notificationInterval:
            casNotifications?.find(
              (notification) => notification.notificationEventId === event.id
            )?.settings?.notificationThresholdDay ?? FrequencyDefault.THRESHOLD,
          time: initialFrequency
            ? moment(
                `${initialFrequency.hour}:${initialFrequency.minute}`,
                'h:mm a'
              )
            : moment('08:00', 'h:mm a'),
        },
        onClear: async () => {
          await edit({
            variables: {
              input: {
                notificationEventId: event.id,
                projectId,
                every: { userIds: [], nonUserEmailIds: [] },
                byRoutingGroup: [],
                settings: null,
              },
            },
          })
        },
        onSave: async ({
          every,
          routingGroups,
          notificationInterval,
          time,
        }) => {
          await edit({
            variables: {
              input: {
                notificationEventId: event.id,
                projectId,
                every: recipientsSelectContext.fromLabeledValues(every),
                byRoutingGroup:
                  routingGroups?.map(({ routingGroupId, recipients }) => ({
                    routingGroupId,
                    recipients:
                      recipientsSelectContext.fromLabeledValues(recipients),
                  })) ?? [],
                settings: {
                  threshold: notificationInterval ?? FrequencyDefault.THRESHOLD,
                  frequency: {
                    hour: time?.hour() ?? FrequencyDefault.HOUR,
                    minute: time?.minutes() ?? FrequencyDefault.MINUTE,
                  },
                },
              },
            },
          })
          refetch()
        },
        render: <RenderFormItems />,
      }
    },
    [
      edit,
      projectId,
      recipients,
      refetch,
      recipientsSelectContext,
      casNotifications,
    ]
  )
}

type InitialValues = {
  every: LabeledValueType[]
  routingGroups: ByRoutingGroupsFormItemValue[]
  frequency?: DailyFrequency
  hour?: number
  minutes?: number
  notificationInterval?: number
  frequencyType?: string
  time?: Moment
}
